import { Result } from 'antd';
import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import {
  RedirectIfAuthenticated,
  RequireAuth,
  RequireFeature,
  RequirePermission,
  RequirePharmacy,
} from '@/auth/guards';
import type { Permission } from '@/auth/permissions';
import { AppLayout } from '@/layout/AppLayout';
import { AppSplash } from '@/components/Loader';
import { SettingsLayout } from '@/features/settings/SettingsLayout';
import { RouteError } from '@/layout/RouteError';
import { isStaleBuildError, reloadForNewBuild } from '@/lib/stale-build';

/*
 * Pages load on demand. Antd is a large dependency and the sign-in screen in
 * particular should not carry the batch receipt form and the ledger with it -
 * this app is opened cold on a tablet at the start of a shift.
 */
/*
 * A page whose file has gone - the tab outlived a deploy - reloads onto the
 * current build rather than failing. The promise that never settles keeps the
 * loading line up until the reload takes over, so the error screen does not
 * flash in between. See `lib/stale-build`.
 */
const load = <T extends Record<string, ComponentType>>(loader: () => Promise<T>, name: keyof T) =>
  lazy(() =>
    loader().then(
      (module) => ({ default: module[name] }),
      (error: unknown) => {
        if (isStaleBuildError(error) && reloadForNewBuild()) {
          return new Promise<never>(() => {});
        }
        throw error;
      },
    ),
  );

const LoginPage = load(() => import('@/features/auth/LoginPage'), 'LoginPage');
const TodayPage = load(() => import('@/features/today/TodayPage'), 'TodayPage');
const SaleEntryPage = load(() => import('@/features/counter/SaleEntryPage'), 'SaleEntryPage');
const RegisterPage = load(() => import('@/features/register/RegisterPage'), 'RegisterPage');
const PurchaseEntryPage = load(
  () => import('@/features/purchase/PurchaseEntryPage'),
  'PurchaseEntryPage',
);
const PurchasesPage = load(() => import('@/features/purchase/PurchasesPage'), 'PurchasesPage');
const SuppliersPage = load(() => import('@/features/purchase/SuppliersPage'), 'SuppliersPage');
const PurchaseImportPage = load(
  () => import('@/features/purchase/PurchaseImportPage'),
  'PurchaseImportPage',
);
const StockPage = load(() => import('@/features/inventory/StockPage'), 'StockPage');
const BatchesPage = load(() => import('@/features/inventory/BatchesPage'), 'BatchesPage');
const AlertsPage = load(() => import('@/features/inventory/AlertsPage'), 'AlertsPage');
const LedgerPage = load(() => import('@/features/inventory/LedgerPage'), 'LedgerPage');
const OrderQueuePage = load(() => import('@/features/orders/OrderQueuePage'), 'OrderQueuePage');
const HoursPage = load(() => import('@/features/hours/HoursPage'), 'HoursPage');
const DeviceSettingsPage = load(
  () => import('@/features/settings/DeviceSettingsPage'),
  'DeviceSettingsPage',
);
const OfflineSettingsPage = load(
  () => import('@/features/settings/OfflineSettingsPage'),
  'OfflineSettingsPage',
);
const CounterSettingsPage = load(
  () => import('@/features/settings/CounterSettingsPage'),
  'CounterSettingsPage',
);
const KeysSettingsPage = load(
  () => import('@/features/settings/KeysSettingsPage'),
  'KeysSettingsPage',
);
const ShopSettingsPage = load(
  () => import('@/features/settings/ShopSettingsPage'),
  'ShopSettingsPage',
);
const TroubleshootingPage = load(
  () => import('@/features/settings/TroubleshootingPage'),
  'TroubleshootingPage',
);
const ReviewQueuePage = load(
  () => import('@/features/prescriptions/ReviewQueuePage'),
  'ReviewQueuePage',
);
const PrescriptionReviewPage = load(
  () => import('@/features/prescriptions/PrescriptionReviewPage'),
  'PrescriptionReviewPage',
);

/**
 * Wraps a page in its guards and a suspense fallback.
 *
 * Every page in this app is inside a branch, so `RequirePharmacy` is applied
 * here rather than per route - forgetting it on one screen is how a request
 * goes out with an empty pharmacy id in the path.
 */
function guarded(
  element: ReactNode,
  permission?: Permission,
  /** An entitlement the branch must hold, on top of the caller's permission. */
  feature?: { key: 'marketplace' | 'prescriptions'; title: string },
): ReactNode {
  /*
   * No loading boundary here. One boundary per page meant every navigation
   * mounted a NEW boundary, and a new boundary always shows its fallback: a
   * whole-page skeleton, then the page's own list skeleton, back to back. The
   * one boundary sits around the outlet in AppLayout instead, and since
   * navigations run as transitions, the page on screen simply stays put while
   * the next one's code arrives.
   */
  let node: ReactNode = <RequirePharmacy>{element}</RequirePharmacy>;
  if (feature) {
    node = (
      <RequireFeature feature={feature.key} title={feature.title}>
        {node}
      </RequireFeature>
    );
  }
  return permission ? <RequirePermission permission={permission}>{node}</RequirePermission> : node;
}

const protectedRoutes: RouteObject[] = [
  { index: true, element: guarded(<TodayPage />) },

  { path: 'sale', element: guarded(<SaleEntryPage />, 'order:fulfil') },
  { path: 'register', element: guarded(<RegisterPage />, 'order:read:any') },
  /*
   * The old Bills route. Kept as a redirect rather than deleted: the bills
   * screen was linkable, sat in the sidebar for months, and this app is run off
   * tablets with pinned tabs and bookmarks. A 404 would read as the shop having
   * lost its bills.
   */
  { path: 'bills', element: <Navigate to="/register?ch=COUNTER" replace /> },

  {
    path: 'prescriptions',
    element: guarded(<ReviewQueuePage />, 'prescription:review', {
      key: 'prescriptions',
      title: 'Prescription review',
    }),
  },
  {
    path: 'prescriptions/:prescriptionId',
    element: guarded(<PrescriptionReviewPage />, 'prescription:review'),
  },

  { path: 'purchase', element: guarded(<PurchaseEntryPage />, 'purchase:write') },
  { path: 'purchase/import', element: guarded(<PurchaseImportPage />, 'purchase:write') },
  { path: 'purchases', element: guarded(<PurchasesPage />, 'purchase:read') },
  { path: 'suppliers', element: guarded(<SuppliersPage />, 'purchase:read') },

  {
    path: 'orders',
    element: guarded(<OrderQueuePage />, 'order:read:any', {
      key: 'marketplace',
      title: 'Online orders',
    }),
  },

  { path: 'stock', element: guarded(<StockPage />, 'inventory:read') },
  { path: 'batches', element: guarded(<BatchesPage />, 'inventory:read') },
  { path: 'alerts', element: guarded(<AlertsPage />, 'inventory:read') },
  { path: 'ledger', element: guarded(<LedgerPage />, 'inventory:read') },

  // Read-only for staff without operations rights; the page hides its own controls.
  { path: 'hours', element: guarded(<HoursPage />, 'pharmacy:read') },

  /*
   * No permission on any of them. Most of what is here belongs to the browser
   * this is running in - storage, alerts, the offline copy - and locking a
   * pharmacist out of their own device's settings because their role cannot
   * read the pharmacy record would be absurd. The shop's entitlements are
   * read-only, and everything behind them is still guarded at the server.
   *
   * Five routes rather than one long page. They were five blocks on one screen
   * and the screen had grown past the point where anyone scrolled to the end of
   * it - the troubleshooting controls, which are the ones somebody is talked
   * through over the phone, were below eight hundred pixels of permission rows.
   */
  {
    path: 'settings',
    // The tabs above every settings page; each page keeps its own guard.
    element: <SettingsLayout />,
    children: [
      { index: true, element: guarded(<DeviceSettingsPage />) },
      { path: 'offline', element: guarded(<OfflineSettingsPage />) },
      { path: 'counter', element: guarded(<CounterSettingsPage />) },
      { path: 'keyboard', element: guarded(<KeysSettingsPage />) },
      { path: 'shop', element: guarded(<ShopSettingsPage />) },
      { path: 'troubleshooting', element: guarded(<TroubleshootingPage />) },
    ],
  },

  {
    path: '*',
    element: <Result status="404" title="No such page" subTitle="That route does not exist." />,
  },
];

export const router = createBrowserRouter([
  {
    path: '/login',
    errorElement: <RouteError />,
    element: (
      <RedirectIfAuthenticated>
        <Suspense fallback={<AppSplash message="Opening sign-in" />}>
          <LoginPage />
        </Suspense>
      </RedirectIfAuthenticated>
    ),
  },
  {
    element: <RequireAuth />,
    // The last resort, for a failure in the chrome itself.
    errorElement: <RouteError />,
    children: [
      {
        path: '/',
        element: <AppLayout />,
        /*
         * A page that fails is caught INSIDE the layout, so the sidebar and
         * header stay up and the way to another page is still on screen.
         */
        children: [{ errorElement: <RouteError />, children: protectedRoutes }],
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
